import type { Article } from '../types';

export const introductionToQsl: Article = {
  slug: 'introduction-to-qsl',
  title: 'Introduction to QSL',
  subtitle: 'Turn Your Trading Logic Into Code',
  description:
    'Learn how QSL turns plain-language trading ideas into testable, optimizable strategies. A practical introduction to the three functions every strategy needs.',
  difficulty: 'intermediate',
  readTime: '18 min',
  category: 'strategy-building',
  tags: ['qsl', 'quanthop strategy language', 'strategy code', 'indicators', 'backtest', 'trading automation'],
  published: true,
  datePublished: '2025-12-01',
  dateModified: '2026-03-27',
  sections: [
    {
      id: 'qsl-overview',
      title: 'Introduction',
      content: `<p>You have a trading idea. Maybe it is an EMA crossover. Maybe it is an RSI mean-reversion setup. You can describe it in a sentence, draw it on a chart, and you are fairly sure it works.</p>
<p>But until the idea is written in a form a machine can execute — with precise entry conditions, exact exit rules, and defined parameters — it cannot be backtested. And if it cannot be backtested, you have no evidence it works. Only a feeling.</p>
<p>QSL is the bridge between the idea in your head and a testable system. It is a lightweight strategy language built into Quanthop that lets you express trading logic as three simple functions. You do not need a computer science degree. If you can describe your strategy in plain English, you can write it in QSL.</p>
<p>This article covers the core concepts. For full API reference and every available indicator, see the <a href="/docs/getting-started/overview">documentation</a>.</p>`,
    },
    {
      id: 'what-is-qsl',
      title: 'What QSL Is (and What It Is Not)',
      content: `<p>QSL stands for <strong>Quanthop Strategy Language</strong>. It is not a general-purpose programming language. You cannot build a website with it or query a database. It does exactly one thing: describe how a trading strategy should behave, bar by bar, across a price series.</p>
<p>A QSL strategy is a JavaScript file with three functions:</p>
<ul>
<li><strong><code>define()</code></strong> — Declare your parameters (the knobs you can tune).</li>
<li><strong><code>init()</code></strong> — Declare your indicators (what to calculate from price data).</li>
<li><strong><code>onBar()</code></strong> — Your trading logic (what to do on each candle).</li>
</ul>
<p>That is the entire contract. Three functions. The backtesting engine handles everything else — fetching candle data, computing indicators, simulating fills, tracking positions, and calculating performance metrics.</p>
<p><strong>Key idea:</strong> You describe <em>what</em> the strategy should do. The engine handles <em>how</em> it executes.</p>`,
    },
    {
      id: 'three-functions',
      title: 'The Three Functions',
      content: `<p>Every QSL strategy follows the same lifecycle. Understanding this lifecycle is the single most important concept in QSL, because it determines when your code runs and what data is available at each stage.</p>
<p><strong>1. <code>define()</code></strong> runs once when the strategy loads. Its only job is to declare parameters — the values you want to adjust and optimize. Think of it as filling out a form: "My strategy has a fast EMA period, a slow EMA period, and a stop-loss multiplier."</p>
<pre><code>function define(ctx) {
ctx.param('fastLength', {
  type: 'int', label: 'Fast EMA', default: 9,
  min: 5, max: 50, step: 5, optimize: true
});
ctx.param('slowLength', {
  type: 'int', label: 'Slow EMA', default: 21,
  min: 10, max: 200, step: 10, optimize: true
});
}</code></pre>
<p>Each parameter has a type, a default value, and an allowed range. The <code>optimize: true</code> flag tells the engine this parameter should be included in optimization and Walk-Forward Analysis. For the full parameter API, see the <a href="/docs/api/lifecycle/define">define() reference</a>.</p>
<p><strong>2. <code>init()</code></strong> runs once before the backtest begins. This is where you declare which indicators to compute. You do not calculate them yourself — you tell the engine what you need, and it pre-computes them across the entire price series.</p>
<pre><code>function init(ctx) {
ctx.indicator('fastEma', 'EMA', {
  period: ctx.p.fastLength, source: 'close', display: true
});
ctx.indicator('slowEma', 'EMA', {
  period: ctx.p.slowLength, source: 'close', display: true
});
}</code></pre>
<p>Notice that indicator periods reference <code>ctx.p.fastLength</code> — the parameter declared in <code>define()</code>. This connection is what makes optimization work: when the engine varies <code>fastLength</code> from 5 to 50, the EMA recalculates automatically. The full list of available indicators is in the <a href="/docs/api/indicators/sma">indicator reference</a>.</p>
<p><strong>3. <code>onBar()</code></strong> runs once per candle, from the first bar to the last. This is your trading logic — the part that decides whether to buy, sell, or do nothing.</p>
<pre><code>function onBar(ctx, i) {
const { fastEma, slowEma } = ctx.ind;

if (q.isNaN(fastEma[i]) || q.isNaN(slowEma[i])) return;

if (q.crossOver(fastEma, slowEma, i)) {
  ctx.order.market('ASSET', 1, {
    signal: 'buy', reason: 'fast_crosses_above_slow'
  });
}
if (q.crossUnder(fastEma, slowEma, i)) {
  ctx.order.close('ASSET', {
    signal: 'sell', reason: 'fast_crosses_below_slow'
  });
}
}</code></pre>
<p>The <code>i</code> parameter is the current bar index. You access indicator values with <code>ctx.ind.fastEma[i]</code> and price data with <code>ctx.series.close[i]</code>. The <code>q</code> namespace provides helper functions like <code>crossOver</code>, <code>crossUnder</code>, and <code>isNaN</code>. For the full order and series API, see the <a href="/docs/api/lifecycle/onBar">onBar() reference</a>.</p>
<p><strong>Key idea:</strong> <code>define()</code> sets the knobs. <code>init()</code> requests the data. <code>onBar()</code> makes the decisions. Everything else is handled by the engine.</p>`,
    },
    {
      id: 'parameters',
      title: 'Parameters: The Knobs You Tune',
      content: `<p>Parameters are what separate a rigid script from a strategy you can test properly. A hardcoded EMA period of 20 gives you one backtest. A parameter that ranges from 10 to 50 gives you a surface you can explore — and more importantly, validate.</p>
<p>In <code>define()</code>, each parameter declaration includes:</p>
<ul>
<li><strong>Type</strong> — <code>int</code>, <code>float</code>, <code>string</code>, or <code>boolean</code>. Most parameters are <code>int</code> or <code>float</code>.</li>
<li><strong>Default</strong> — The starting value for a simple backtest.</li>
<li><strong>Range</strong> — <code>min</code>, <code>max</code>, and <code>step</code> define the optimization search space.</li>
<li><strong>Optimize flag</strong> — Only parameters with <code>optimize: true</code> are included in Walk-Forward Analysis.</li>
</ul>
<p>A common mistake is making every number a parameter. If your strategy has 8 optimizable parameters, the search space is enormous and overfitting becomes almost certain. Start with 2–3 core parameters. Add more only when the strategy is already profitable with the simple version.</p>
<p><strong>Key idea:</strong> Parameters are not just convenience — they are the foundation of optimization and validation. Choose them deliberately.</p>`,
    },
    {
      id: 'indicators',
      title: 'Indicators: What the Engine Calculates',
      content: `<p>Indicators are declared in <code>init()</code> and computed by the engine before your <code>onBar()</code> function ever runs. This means you never write the EMA formula yourself — you tell the engine you need an EMA with a certain period, and it delivers a pre-computed array of values.</p>
<p>Why does this matter? Two reasons. First, the engine computes indicators in optimized native code (typed arrays, not JavaScript loops), which is significantly faster. Second, it eliminates an entire category of bugs — your indicator math is always correct because the engine handles it.</p>
<p>Available indicators include <strong>SMA</strong>, <strong>EMA</strong>, <strong>RSI</strong>, <strong>MACD</strong>, <strong>Bollinger Bands</strong>, <strong>ATR</strong>, <strong>Stochastic</strong>, <strong>Donchian Channels</strong>, and more. Each has a specific configuration. For example, MACD requires three periods:</p>
<pre><code>ctx.indicator('macd', 'MACD', {
fastPeriod: 12, slowPeriod: 26, signalPeriod: 9,
source: 'close'
});</code></pre>
<p>In <code>onBar()</code>, MACD exposes three values: <code>ctx.ind.macd.macd[i]</code>, <code>ctx.ind.macd.signal[i]</code>, and <code>ctx.ind.macd.histogram[i]</code>. The full indicator catalogue with configuration options is in the <a href="/docs/api/indicators/sma">indicator reference</a>.</p>
<p>The <code>display: true</code> flag tells the chart to overlay the indicator on your backtest results. It does not affect execution — it is purely visual.</p>`,
    },
    {
      id: 'orders',
      title: 'Placing Orders',
      content: `<p>When your <code>onBar()</code> logic decides to trade, it places an order. QSL supports four order methods:</p>
<ul>
<li><strong><code>ctx.order.market(symbol, qty, meta)</code></strong> — Open a position at the next bar's open price. The most common order type. Use <code>qty: 1</code> for a full long, <code>0.5</code> for a half position, or <code>-1</code> for a full short.</li>
<li><strong><code>ctx.order.close(symbol, meta)</code></strong> — Close the entire position at the next bar's open. No quantity needed — it closes everything.</li>
<li><strong><code>ctx.order.reduce(symbol, fraction, meta)</code></strong> — Partially close a position by a fraction. <code>reduce('ASSET', 0.5)</code> closes half.</li>
<li><strong><code>ctx.order.limit(symbol, qty, price, meta)</code></strong> — Buy only if price reaches a specified level. Expires unfilled if price never touches the limit.</li>
</ul>
<p>A critical detail: <strong>orders placed on bar <em>i</em> fill on bar <em>i+1</em></strong>. This is how real trading works — you see a signal on the current candle and act on the next one. The engine enforces this automatically, which prevents look-ahead bias. For the full execution model, see the <a href="/docs/engine/execution/order-timing">order timing documentation</a>.</p>
<p><strong>Scaling in and out.</strong> These four methods combine to support sophisticated position management. A strategy can use <code>market()</code> with a fractional quantity to take a small pre-entry position on an early signal, then add to it with another <code>market()</code> when the full signal confirms. On the exit side, <code>reduce()</code> lets you take partial profit at one level while holding the remainder for a larger move. The built-in TDR strategy uses exactly this pattern — a partial <code>preEntry</code> allocation, a full entry on crossover confirmation, partial <code>takeProfit</code> exits, and a full exit on reversal.</p>
<p>The <code>meta</code> object is optional metadata — <code>signal</code>, <code>reason</code>, and <code>type</code> fields that appear in your trade log. Use them liberally. When you are reviewing 200 trades, knowing <em>why</em> each one triggered is invaluable.</p>
<pre><code>// Partial entry on early signal
ctx.order.market('ASSET', 0.3, {
signal: 'preEntry', type: 'partial_entry',
reason: 'price_crosses_short_baseline'
});

// Full entry on confirmation
ctx.order.market('ASSET', 1, {
signal: 'buy', type: 'full_entry',
reason: 'ema_crossover_confirmed'
});

// Partial profit taking
ctx.order.reduce('ASSET', 0.5, {
signal: 'takeProfit', type: 'partial_exit',
reason: 'target_reached'
});</code></pre>
<p><strong>Key idea:</strong> You place the order. The engine fills it on the next bar. This one-bar delay is not a limitation — it is how you avoid the most common backtesting mistake.</p>`,
    },
    {
      id: 'helper-functions',
      title: 'Helper Functions',
      content: `<p>The <code>q</code> namespace provides helper functions that simplify common trading logic. You do not need to write crossover detection from scratch.</p>
<ul>
<li><strong><code>q.crossOver(a, b, i)</code></strong> — Returns <code>true</code> when series <code>a</code> crosses above series <code>b</code> at bar <code>i</code>.</li>
<li><strong><code>q.crossUnder(a, b, i)</code></strong> — Returns <code>true</code> when series <code>a</code> crosses below series <code>b</code> at bar <code>i</code>.</li>
<li><strong><code>q.isNaN(value)</code></strong> — Checks if a value is ready. Indicators return <code>NaN</code> during their warmup period (e.g., a 20-period EMA has no valid value for the first 19 bars).</li>
<li><strong><code>q.highest(series, period, i)</code></strong> — The highest value in the last <code>period</code> bars.</li>
<li><strong><code>q.lowest(series, period, i)</code></strong> — The lowest value in the last <code>period</code> bars.</li>
</ul>
<p>The <code>q.isNaN()</code> check deserves special attention. Without it, your strategy will attempt to trade on invalid indicator values during the warmup period. Always guard your <code>onBar()</code> logic:</p>
<pre><code>if (q.isNaN(ctx.ind.rsi[i])) return;</code></pre>
<p>This single line prevents an entire class of early-bar errors.</p>`,
    },
    {
      id: 'complete-example',
      title: 'A Complete Strategy',
      content: `<p>Here is a full RSI mean-reversion strategy in QSL. It buys when RSI exits oversold territory and the price is above a long-term EMA (trend filter). It sells when RSI reaches overbought levels.</p>
<pre><code>function define(ctx) {
ctx.param('rsiPeriod', {
  type: 'int', label: 'RSI Period', default: 14,
  min: 7, max: 28, step: 1, optimize: true
});
ctx.param('oversold', {
  type: 'int', label: 'Oversold Threshold', default: 30,
  min: 15, max: 40, step: 5, optimize: true
});
ctx.param('overbought', {
  type: 'int', label: 'Overbought Threshold', default: 70,
  min: 60, max: 85, step: 5, optimize: true
});
}

function init(ctx) {
ctx.indicator('rsi', 'RSI', {
  period: ctx.p.rsiPeriod, source: 'close'
});
ctx.indicator('trendEma', 'EMA', {
  period: 200, source: 'close', display: true
});
}

function onBar(ctx, i) {
const rsi = ctx.ind.rsi[i];
const ema = ctx.ind.trendEma[i];
const close = ctx.series.close[i];

if (q.isNaN(rsi) || q.isNaN(ema)) return;

const prevRsi = ctx.ind.rsi[i - 1];

// Enter: RSI crosses above oversold, price above trend
if (prevRsi <= ctx.p.oversold && rsi > ctx.p.oversold
    && close > ema) {
  ctx.order.market('ASSET', 1, {
    signal: 'buy', reason: 'rsi_exits_oversold'
  });
}

// Exit: RSI reaches overbought
if (rsi >= ctx.p.overbought) {
  ctx.order.close('ASSET', {
    signal: 'sell', reason: 'rsi_overbought'
  });
}
}</code></pre>
<p>Walk through it section by section:</p>
<ul>
<li><strong><code>define()</code></strong> declares three parameters with sensible defaults and optimization ranges. The step sizes are small enough to find good values but large enough to keep the search space manageable.</li>
<li><strong><code>init()</code></strong> requests two indicators — an RSI that uses the optimizable period, and a 200-period EMA as a fixed trend filter.</li>
<li><strong><code>onBar()</code></strong> checks the RSI crossing above the oversold level while price is above the trend EMA. It exits when RSI reaches overbought. The <code>i - 1</code> comparison detects the crossing event (previous bar below threshold, current bar above).</li>
</ul>
<p>This strategy is ready to backtest. It has clear entry and exit logic, defined parameters with reasonable ranges, and a trend filter to avoid counter-trend trades. You could run Walk-Forward Analysis on it immediately to validate whether the RSI and threshold parameters are robust across time.</p>`,
    },
    {
      id: 'state',
      title: 'Using State',
      content: `<p>Sometimes your strategy needs to remember something between bars. Maybe you want to track how many bars since the last signal, or store a high watermark. That is what <code>ctx.state</code> is for.</p>
<p>You initialize state variables in <code>init()</code>:</p>
<pre><code>function init(ctx) {
ctx.indicator('ema', 'EMA', { period: ctx.p.length, source: 'close' });
ctx.state.barsSinceEntry = 0;
ctx.state.entryPrice = 0;
}</code></pre>
<p>Then read and write them in <code>onBar()</code>:</p>
<pre><code>function onBar(ctx, i) {
const pos = ctx.position('ASSET');

if (pos.qty > 0) {
  ctx.state.barsSinceEntry++;

  // Time-based exit: close after 10 bars
  if (ctx.state.barsSinceEntry >= 10) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'time_exit' });
    ctx.state.barsSinceEntry = 0;
  }
}
}</code></pre>
<p>State is useful for time-based exits, trailing stops that need to track a running maximum, or any logic that depends on what happened in previous bars beyond what indicators capture.</p>
<p>Be judicious with state. Every piece of state is something you need to keep correct across hundreds of bars. When you can solve a problem with an indicator or a <code>q.highest()</code> helper instead, prefer that approach.</p>`,
    },
    {
      id: 'common-mistakes',
      title: 'Common Mistakes',
      content: `<p>Most QSL bugs fall into a handful of categories. Knowing them in advance saves hours of debugging.</p>
<p><strong>Forgetting the <code>isNaN</code> guard.</strong> Every indicator has a warmup period. A 50-period EMA produces <code>NaN</code> for the first 49 bars. If your <code>onBar()</code> compares <code>NaN</code> to a threshold, the result is always <code>false</code> — your strategy will never trigger during warmup, which is correct but accidental. Worse, arithmetic on <code>NaN</code> produces <code>NaN</code>, which can cascade through your entire logic. Always check <code>q.isNaN()</code> for every indicator before using it.</p>
<p><strong>Trading on the current bar.</strong> If you see a signal on bar 10 and place an order, that order fills on bar 11. New traders sometimes try to check whether the fill price was good by looking at bar 10's close. It was not available at the time the order was placed. The engine enforces this, but your mental model should match.</p>
<p><strong>Too many optimizable parameters.</strong> Three parameters with 10 steps each produce 1,000 combinations. Add a fourth and you have 10,000. The more combinations, the more likely that the "best" one exists by chance. Start with 2–3 parameters. If the strategy is not profitable with a few well-chosen knobs, adding more will not fix it — it will only disguise the problem.</p>
<p><strong>Hardcoding values that should be parameters.</strong> If you might ever want to test different values for a number, declare it as a parameter. A hardcoded stop-loss of 2% cannot be optimized. A parameter called <code>stopPct</code> with a range of 1–5% can.</p>
<p><strong>Not using meta on orders.</strong> Six months from now, you will review a strategy with 300 trades and wonder why trade #147 triggered. The <code>signal</code> and <code>reason</code> fields in order meta are free — use them.</p>`,
    },
    {
      id: 'next-steps',
      title: 'Where to Go From Here',
      content: `<p>This article covered the core concepts — the three-function lifecycle, parameters, indicators, orders, helpers, and state. With these building blocks, you can express the vast majority of systematic strategies.</p>
<p>Your next steps:</p>
<ul>
<li><strong>Read the documentation</strong> — The <a href="/docs/getting-started/strategy-lifecycle">strategy lifecycle</a> page covers <code>define()</code>, <code>init()</code>, and <code>onBar()</code> in full detail. The <a href="/docs/api/indicators/sma">indicator reference</a> documents every available indicator and its configuration.</li>
<li><strong>Start with a template</strong> — The Strategy IDE includes starter templates (EMA Crossover, RSI Mean Reversion, Bollinger Breakout, MACD Momentum). Open one, read through it, then modify it to match your idea.</li>
<li><strong>Backtest immediately</strong> — Do not write the "perfect" strategy before testing. Write the simplest version of your idea, backtest it, and iterate based on results. Covered in the next article: <a href="/learn/backtesting-optimization/running-your-first-backtest">Running Your First Backtest</a>.</li>
<li><strong>Keep parameters minimal</strong> — 2–3 optimizable parameters for your first strategy. You can always add complexity after proving the core logic works.</li>
</ul>
<p>QSL is deliberately simple. The complexity in quantitative trading is not in the code — it is in choosing the right logic, validating it honestly, and trusting the results only when they survive out-of-sample testing. QSL stays out of your way so you can focus on what actually matters.</p>`,
    },
  ],
  faq: [
    {
      question: 'What is QSL in Quanthop?',
      answer: 'QSL (Quanthop Strategy Language) is a lightweight strategy framework built into Quanthop that lets you express trading logic as three JavaScript functions: define() for parameters, init() for indicators, and onBar() for trading logic that runs on each candle.'
    },
    {
      question: 'Do I need to know programming to use QSL?',
      answer: 'You do not need a computer science degree. QSL is designed to mirror how traders think. If you can describe your strategy in plain English, you can write it in QSL. The platform provides starter templates you can clone and modify.'
    },
    {
      question: 'How many parameters should a QSL strategy have?',
      answer: 'Start with 2-3 optimizable parameters. Three parameters with 10 steps each produce 1,000 combinations. Adding a fourth creates 10,000. Too many parameters dramatically increases the risk of overfitting to historical noise.'
    },
    {
      question: 'What indicators are available in QSL?',
      answer: 'QSL supports SMA, EMA, RSI, MACD, Bollinger Bands, ATR, Stochastic, Donchian Channels, and more. Indicators are declared in init() and pre-computed by the engine in optimized native code before your trading logic runs.'
    },
    {
      question: 'How do orders work in QSL?',
      answer: 'Orders placed on bar i fill on bar i+1, matching how real trading works. QSL supports market orders, close orders, partial reduce orders, and limit orders. This one-bar delay prevents look-ahead bias automatically.'
    }
  ],
  cta: {
    text: 'Open the Strategy IDE and try QSL',
    href: '/signup',
    type: 'strong',
  },
  relatedSlugs: ['entry-exit-rules', 'running-your-first-backtest', 'anatomy-of-a-strategy', 'parameter-optimization'],
};
